export const SMARTLINK_QUERY_PARAM = 'smartlink';

const SMARTLINK_CACHE_KEY = 'wework.smartlink-resolution.v3';
const SMARTLINK_ACTIVE_FLAG_KEY = 'wework.contieneSmart';
const SMARTLINK_ACTIVE_VALUE_KEY = 'wework.smartlink-activo';
const SMARTLINK_IFRAME_NAVIGATION_KEY = 'wework.smartlink-iframe-navigation';
const SMARTLINK_CACHE_TTL_MS = 5 * 60 * 1000;
const SMARTLINK_IFRAME_NAVIGATION_TTL_MS = 2 * 60 * 1000;
const DEFAULT_SMARTLINK_API_URL = 'https://serverwework.koppi.mx';

const isBrowser = () => typeof window !== 'undefined';

const normalizeEmail = (value) => {
  const emailText = String(value || '').trim();
  if (!emailText || emailText === '-') return '-';

  const email = emailText.toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
};

const normalizeParamKey = (value) => {
  try {
    return decodeURIComponent(String(value || '').replace(/\+/g, ' ')).trim().toLowerCase();
  } catch {
    return String(value || '').trim().toLowerCase();
  }
};

export const normalizeSmartlink = (value) => {
  const normalized = String(value || '').trim();
  return /^[a-zA-Z0-9]{4,160}$/.test(normalized) ? normalized : '';
};

// Convierte un smartlink en un nombre legible: 'GuillermoJuan2203' -> 'Guillermo Juan'.
// Quita el sufijo numerico y separa por camelCase.
export const smartlinkToName = (smartlink) => {
  const slug = normalizeSmartlink(smartlink);
  if (!slug) return '';
  const withoutDigits = slug.replace(/\d+$/, '');
  const words = withoutDigits.match(/[A-Z][a-z]*/g);
  return words && words.length ? words.join(' ') : '';
};

const getRawSearchParts = (search = '') => String(search || '')
  .replace(/^\?/, '')
  .split('&')
  .filter(Boolean);

const isSmartlinkPart = (part) => {
  const separatorIndex = String(part || '').indexOf('=');
  const rawKey = separatorIndex >= 0 ? part.slice(0, separatorIndex) : part;
  return normalizeParamKey(rawKey) === SMARTLINK_QUERY_PARAM;
};

export const getSmartlinkFromSearch = (search = '') => {
  let smartlink = '';

  for (const part of getRawSearchParts(search)) {
    if (!isSmartlinkPart(part)) continue;

    const separatorIndex = part.indexOf('=');
    const rawValue = separatorIndex >= 0 ? part.slice(separatorIndex + 1) : '';

    try {
      smartlink = normalizeSmartlink(decodeURIComponent(rawValue.replace(/\+/g, ' ')));
    } catch {
      smartlink = '';
    }
  }

  return smartlink;
};

export const ensureSmartlinkLast = (search = '', smartlink = '') => {
  const normalizedSmartlink = normalizeSmartlink(smartlink);
  const parts = getRawSearchParts(search).filter((part) => !isSmartlinkPart(part));

  if (normalizedSmartlink) {
    parts.push(`${SMARTLINK_QUERY_PARAM}=${encodeURIComponent(normalizedSmartlink)}`);
  }

  return parts.length ? `?${parts.join('&')}` : '';
};

export const clearStoredSmartlink = () => {
  if (!isBrowser()) return;
  window.localStorage.removeItem(SMARTLINK_CACHE_KEY);
};

export const readStoredSmartlink = (smartlink, now = Date.now()) => {
  if (!isBrowser()) return null;

  const normalizedSmartlink = normalizeSmartlink(smartlink);
  if (!normalizedSmartlink) return null;

  try {
    const stored = JSON.parse(window.localStorage.getItem(SMARTLINK_CACHE_KEY) || 'null');
    const storedSmartlink = normalizeSmartlink(stored?.smartlink);
    const resolvedAt = Number(stored?.resolvedAt);
    const isFresh = Number.isFinite(resolvedAt)
      && resolvedAt > 0
      && now - resolvedAt <= SMARTLINK_CACHE_TTL_MS;

    if (storedSmartlink.toLowerCase() !== normalizedSmartlink.toLowerCase() || !isFresh) {
      clearStoredSmartlink();
      return null;
    }

    if (stored?.found === false) {
      return { smartlink: normalizedSmartlink, found: false, whatsapp: '', email: '', resolvedAt };
    }

    const whatsapp = String(stored?.whatsapp || '').replace(/\D/g, '');
    if (stored?.found !== true || whatsapp.length < 11 || whatsapp.length > 15) {
      clearStoredSmartlink();
      return null;
    }

    // El email es complementario; si falta o viene mal formado se descarta sin invalidar el caché.
    const email = normalizeEmail(stored?.email);
    return { smartlink: normalizedSmartlink, found: true, whatsapp, email, resolvedAt };
  } catch {
    clearStoredSmartlink();
    return null;
  }
};

export const storeSmartlinkResolution = ({ smartlink, found, whatsapp = '', email = '' }) => {
  if (!isBrowser()) return;

  const normalizedSmartlink = normalizeSmartlink(smartlink);
  if (!normalizedSmartlink) {
    clearStoredSmartlink();
    return;
  }

  window.localStorage.setItem(SMARTLINK_CACHE_KEY, JSON.stringify({
    smartlink: normalizedSmartlink,
    found: found === true,
    whatsapp: found === true ? String(whatsapp || '').replace(/\D/g, '') : '',
    email: found === true ? normalizeEmail(email) : '',
    resolvedAt: Date.now(),
  }));
};

export const rememberActiveSmartlink = (smartlink) => {
  if (!isBrowser()) return;

  const normalizedSmartlink = normalizeSmartlink(smartlink);
  if (!normalizedSmartlink) return;

  try {
    window.localStorage.setItem(SMARTLINK_ACTIVE_FLAG_KEY, 'true');
    window.localStorage.setItem(SMARTLINK_ACTIVE_VALUE_KEY, normalizedSmartlink);
  } catch {
    // El Smartlink de la URL sigue funcionando aunque el storage no esté disponible.
  }
};

export const disableActiveSmartlink = () => {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(SMARTLINK_ACTIVE_FLAG_KEY, 'false');
    window.localStorage.removeItem(SMARTLINK_ACTIVE_VALUE_KEY);
  } catch {
    // Sin acción: el estado actual de React mantiene los datos generales.
  }
};

export const getPersistedActiveSmartlink = () => {
  if (!isBrowser()) return '';

  try {
    if (window.localStorage.getItem(SMARTLINK_ACTIVE_FLAG_KEY) !== 'true') return '';
    return normalizeSmartlink(window.localStorage.getItem(SMARTLINK_ACTIVE_VALUE_KEY));
  } catch {
    return '';
  }
};

export const markSmartlinkIframeNavigation = () => {
  if (!isBrowser() || !getPersistedActiveSmartlink()) return;

  try {
    window.sessionStorage.setItem(SMARTLINK_IFRAME_NAVIGATION_KEY, String(Date.now()));
  } catch {
    // Sin acción: algunos navegadores pueden bloquear sessionStorage dentro de iframes.
  }
};

export const hasPendingSmartlinkIframeNavigation = (now = Date.now()) => {
  if (!isBrowser()) return false;

  try {
    const markedAt = Number(window.sessionStorage.getItem(SMARTLINK_IFRAME_NAVIGATION_KEY));
    return Number.isFinite(markedAt)
      && markedAt > 0
      && now - markedAt <= SMARTLINK_IFRAME_NAVIGATION_TTL_MS;
  } catch {
    return false;
  }
};

export const clearSmartlinkIframeNavigation = () => {
  if (!isBrowser()) return;

  try {
    window.sessionStorage.removeItem(SMARTLINK_IFRAME_NAVIGATION_KEY);
  } catch {
    // Sin acción.
  }
};

export const getInitialSmartlinkState = (search = isBrowser() ? window.location.search : '') => {
  const smartlinkFromUrl = getSmartlinkFromSearch(search);
  const smartlink = smartlinkFromUrl || (
    hasPendingSmartlinkIframeNavigation() ? getPersistedActiveSmartlink() : ''
  );
  if (!smartlink) {
    return { smartlink: '', whatsapp: '', email: '', status: 'default' };
  }

  const stored = readStoredSmartlink(smartlink);
  if (!stored) {
    return { smartlink, whatsapp: '', email: '', status: 'loading' };
  }

  return {
    smartlink,
    whatsapp: stored.found ? stored.whatsapp : '',
    email: stored.found ? stored.email : '',
    status: stored.found ? 'resolved' : 'not-found',
  };
};

export const fetchSmartlinkResolution = async (smartlink, { signal } = {}) => {
  const normalizedSmartlink = normalizeSmartlink(smartlink);
  if (!normalizedSmartlink) return { found: false, whatsapp: '', email: '' };

  const configuredApiUrl = String(import.meta.env.VITE_SMARTLINK_API_URL || '').trim();
  const apiUrl = (configuredApiUrl || DEFAULT_SMARTLINK_API_URL).replace(/\/+$/, '');
  const response = await fetch(
    `${apiUrl}/whatsapp/resolve?smartlink=${encodeURIComponent(normalizedSmartlink)}`,
    { signal }
  );

  if (!response.ok) {
    throw new Error(`Smartlink request failed: ${response.status} ${response.statusText}`);
  }

  const payload = await response.json();
  const whatsapp = String(payload?.whatsapp || '').replace(/\D/g, '');
  const found = payload?.found === true && whatsapp.length >= 11 && whatsapp.length <= 15;

  // El email se sanea aparte; found sigue dependiendo solo de whatsapp para no romper la logica actual.
  const email = found ? normalizeEmail(payload?.email) : '';

  return { found, whatsapp: found ? whatsapp : '', email };
};
